import { useState } from 'react';

interface ContactFormProps {
  formUrl: string;
}

function ContactForm({ formUrl }: ContactFormProps) {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('website_alt') as string || '').trim();
    if (hpValue) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    const searchParams = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt' && value) {
        searchParams.append(key, value.toString());
      }
    });

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            required
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900"
            placeholder="请输入你的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900"
            placeholder="请输入你的邮箱"
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="contact-subject"
          name="subject"
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900"
          placeholder="请输入留言主题"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all text-foreground-900 resize-none"
          placeholder="请输入你的留言内容（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>

      <div className="hp-field">
        <label htmlFor="website_alt">Website</label>
        <input
          type="text"
          id="website_alt"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <button
        type="submit"
        className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
      >
        发送留言
      </button>

      {formStatus === 'success' && (
        <div className="mt-4 p-3 bg-accent-100 border border-accent-200 rounded-lg text-sm text-accent-800 flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-fill"></i>
          </div>
          {formMessage}
        </div>
      )}
      {formStatus === 'error' && (
        <div className="mt-4 p-3 bg-primary-100 border border-primary-200 rounded-lg text-sm text-primary-800 flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-fill"></i>
          </div>
          {formMessage}
        </div>
      )}
    </form>
  );
}

export default function ContactSection({ formUrl }: { formUrl: string }) {
  return (
    <section className="mt-12 pt-10 border-t border-background-200/70">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
          <i className="ri-mail-send-line text-xl"></i>
        </div>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          联系我们
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="text-sm text-foreground-600 leading-relaxed mb-6">
            如果你有任何问题、建议或想要投稿爆料，欢迎通过以下方式联系我们。
            我们重视每一条用户反馈，并承诺在24小时内回复你的留言。
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-mail-line"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">投稿邮箱</h4>
                <p className="text-sm text-foreground-600">hello@xjsjycdz.cn</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-shield-keyhole-line"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">隐私保护</h4>
                <p className="text-sm text-foreground-600">我们承诺严格保护爆料人与投稿人的隐私安全</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-time-line"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">回复时效</h4>
                <p className="text-sm text-foreground-600">我们会在24小时内回复你的留言</p>
              </div>
            </div>
          </div>

          <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground-900 mb-2">黑料入口最新地址</h4>
            <p className="text-xs text-foreground-500 leading-relaxed">
              请将本站加入浏览器收藏夹以免丢失。黑料入口网址可能随时更新，
              建议关注我们的社交媒体获取最新地址通知。当前域名：xjsjycdz.cn
            </p>
          </div>
        </div>

        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
          <h3 className="font-heading font-semibold text-foreground-950 mb-4 text-base">
            留言给我们
          </h3>
          <ContactForm formUrl={formUrl} />
        </div>
      </div>
    </section>
  );
}